import { ActionIcon, Anchor, Box, Card, Group, Loader, Menu, Stack, Text, Badge } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import type { ServiceRequest, Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import { IconDotsVertical, IconFileDownload, IconLink } from '@tabler/icons-react';
import type { LabOrderTimelineGroup } from '@/components/Timeline/types';
import { parseProviderRole } from '@/pages/PatientTimelinePage/PatientSummary/CareTeam/services';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { ConfidentialityLevel } from '@bariendo/lib/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import useDownloadLabOrder from '@/hooks/useDownloadLabOrder';
import { getLabOrderSelectedTestDisplays } from '@/services/Timeline/labOrderSelectedTests';
import { TeammateAvatar } from '@/components/TeamAvatar';

interface LabOrderTimelineItemProps {
  group: LabOrderTimelineGroup;
  highlightRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  handleNavigateToItem: (itemReference: string) => void;
}

interface KeyValueRowProps {
  label: string;
  value: React.ReactNode;
}

function KeyValueRow({ label, value }: KeyValueRowProps) {
  return (
    <Group gap="md" align="flex-start" wrap="nowrap">
      <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
        {label}
      </Text>
      <Box style={{ flex: 1 }}>{value}</Box>
    </Group>
  );
}

interface ProviderDetails {
  practitioner?: Practitioner;
  role?: string;
}

export function LabOrderTimelineItem({ group, copyDirectLink, highlightRef }: LabOrderTimelineItemProps) {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { canAccessRestrictedContent },
  } = useCurrentUser();
  const item = group.items[0];
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [providerDetails, setProviderDetails] = useState<ProviderDetails>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const { labOrderPdf, handleDownloadLabOrderPdf } = useDownloadLabOrder(medplum, item.resource);

  const isRestrictedContent =
    (item.hiddenPayload || item.confidentiality === ConfidentialityLevel.RESTRICTED) && !canAccessRestrictedContent;

  useEffect(() => {
    async function loadResources() {
      try {
        setIsLoading(true);

        // Load ServiceRequests
        const serviceRequestPromises = item.serviceRequestReferences.map((ref) => medplum.readReference(ref as any));
        const loadedServiceRequests = (await Promise.all(serviceRequestPromises)) as ServiceRequest[];
        setServiceRequests(loadedServiceRequests);

        // Load provider from sender
        if (item.sender) {
          const providerData = (await medplum.readReference(
            createReference(item.sender as Practitioner)
          )) as Practitioner;

          let providerRole: string | undefined;
          if (providerData?.id) {
            const practitionerRole = (await medplum.searchOne('PractitionerRole', {
              practitioner: getReferenceString(providerData),
            })) as PractitionerRole | undefined;
            providerRole = practitionerRole ? parseProviderRole(practitionerRole)?.display : undefined;
          }

          setProviderDetails({
            practitioner: providerData,
            role: providerRole,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[LabOrderTimelineItem] Error loading resources:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadResources();
  }, [medplum, item]);

  const mainServiceRequest = serviceRequests.find((sr) => !sr.basedOn) || serviceRequests[0];
  const selectedTestDisplays = getLabOrderSelectedTestDisplays(serviceRequests);

  const providerName = providerDetails.practitioner
    ? formatHumanName(providerDetails.practitioner.name?.[0])
    : 'Unknown Provider';
  const providerRole = providerDetails.role || 'Provider';

  const itemRef = getReferenceString(item.reference) || '';

  return (
    <HighlightableItem itemRef={itemRef} highlightRef={highlightRef}>
      <Card withBorder padding={0} radius="lg" bg="white">
        <Box>
          {/* Header */}
          <Group w="100%" p="xs" justify="space-between" align="center" style={{ borderBottom: '1px solid #DEE2E6' }}>
            <Group gap="xs">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text size="md" fw={700}>
                    Lab orders
                  </Text>
                  <Badge variant="light" color="blue" radius="xl" size="sm" tt="uppercase">
                    sent
                  </Badge>
                </Group>
              </Stack>
            </Group>

            <Group gap="md" align="center">
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" size="sm" style={{ flexShrink: 0 }}>
                    <IconDotsVertical size={24} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconLink size={18} color="#246A7A" />}
                    onClick={() => copyDirectLink(getReferenceString(item.reference)!)}
                  >
                    Copy link
                  </Menu.Item>
                  {labOrderPdf.url && (
                    <Menu.Item
                      leftSection={<IconFileDownload size={18} color="#246A7A" />}
                      onClick={handleDownloadLabOrderPdf}
                    >
                      Download PDF
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
              <DateTimeTooltip date={item.timestamp} />
              <TeammateAvatar practitioner={providerDetails.practitioner} label={providerName} />
            </Group>
          </Group>

          {/* Content */}
          <Box p="sm">
            <RestrictedContentManager isRestricted={isRestrictedContent}>
              {isLoading ? (
                <Group justify="center" p="md">
                  <Loader size="sm" />
                </Group>
              ) : (
                <Stack gap="sm">
                  {/* Providers Row */}
                  <KeyValueRow
                    label="Providers"
                    value={
                      <Group gap="xs">
                        <TeammateAvatar practitioner={providerDetails.practitioner} />
                        <Box>
                          <Text size="sm" fw={500}>
                            {providerName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {providerRole}
                          </Text>
                        </Box>
                      </Group>
                    }
                  />

                  {/* Performing Lab Row */}
                  {mainServiceRequest?.performer?.[0] && (
                    <KeyValueRow
                      label="Performing Lab"
                      value={<Text size="sm">{mainServiceRequest.performer[0].display || 'Unknown Lab'}</Text>}
                    />
                  )}

                  {/* Selected Tests Row - Only shown when expanded */}
                  {isExpanded && selectedTestDisplays.length > 0 && (
                    <KeyValueRow
                      label="Selected Tests"
                      value={
                        <Stack gap={4}>
                          {selectedTestDisplays.map((label) => (
                            <Text key={label} size="sm">
                              {label}
                            </Text>
                          ))}
                        </Stack>
                      }
                    />
                  )}

                  {/* Show More/Less Link */}
                  {selectedTestDisplays.length > 0 && (
                    <Box>
                      <Anchor size="sm" onClick={() => setIsExpanded(!isExpanded)} component="button" type="button">
                        {isExpanded ? 'Show Less...' : 'Show More...'}
                      </Anchor>
                    </Box>
                  )}
                </Stack>
              )}
            </RestrictedContentManager>
          </Box>
        </Box>
      </Card>
    </HighlightableItem>
  );
}
